query designWorkflowStages {
  designWorkflowStages {
    value
    label
    badgeVariant
  }
}

query designTypes {
  designTypes {
    value
    label
    badgeVariant
  }
}

# Separate from designTypes. A schema without sourceWorkspaceId fails this
# query only; the hub still loads names and the built-in baseline picker.
query designTypeSourceWorkspaces {
  designTypeSources: designTypes {
    value
    sourceWorkspaceId
  }
}

# What the business-area search needs from a design's source workspace. Run
# with a client for that workspace (WorkspaceApolloProvider).
query getDesignSourceWorkspace($id: ID!) {
  workspace: getWorkspace(id: $id) {
    id
    name
    config {
      groupTypes
      featureFlags
      allocation {
        enableChangeSupplyGroup
      }
    }
  }
}

fragment DesignDeprovisioningFields on DesignDeprovisioning {
  removingUserAccess {
    status
    updatedAt
  }
  deleteWorkspace {
    status
    updatedAt
  }
  removeFromHub {
    status
    updatedAt
  }
}

query listDesigns {
  designs: listDesigns {
    id
    name
    designType
    baselineWorkspaceId
    designWorkspaceId
    deprovisioning {
      ...DesignDeprovisioningFields
    }
    sponsors {
      aggregateId
      preferredName
      firstName
      surname
      email
      avatar
      jobTitle
    }
    leads {
      aggregateId
      preferredName
      firstName
      surname
      email
      avatar
      jobTitle
    }
    participants {
      aggregateId
      preferredName
      firstName
      surname
      email
      avatar
      jobTitle
    }
    scopeRoots {
      workspaceId
      groupId
      group {
        id
        name
        type
        hierarchy
        hierarchyIds
      }
    }
    purpose
    benefits
    workflowStage
    startDate
    targetImplementationDate
    baselineDataDate
    createdAt
    updatedAt
  }
}

query listDesignWorkspaceIds {
  designs: listDesigns {
    id
    designWorkspaceId
  }
}

query getDesignDeprovisioningStatus($id: ID!) {
  design: getDesign(id: $id) {
    id
    deprovisioning {
      ...DesignDeprovisioningFields
    }
  }
}

query getDesignProvisioningStatus($id: ID!) {
  design: getDesign(id: $id) {
    id
    designWorkspaceId
    provisioning {
      clone {
        status
        updatedAt
      }
      dataSync {
        status
        updatedAt
      }
      roleAssignment {
        status
        updatedAt
      }
    }
  }
}

# Separate from getDesignProvisioningStatus so a schema without configure
# leaves the clone, sync and role steps working.
query getDesignConfigureStatus($id: ID!) {
  design: getDesign(id: $id) {
    id
    provisioning {
      configure {
        status
        updatedAt
        designTypeVersion
        error
      }
    }
  }
}

query getDesign($id: ID!) {
  design: getDesign(id: $id) {
    id
    tenantId
    name
    designType
    canEditPeople
    baselineWorkspaceId
    designWorkspaceId
    deprovisioning {
      ...DesignDeprovisioningFields
    }
    sponsors {
      aggregateId
      preferredName
      firstName
      surname
      email
      avatar
      jobTitle
    }
    leads {
      aggregateId
      preferredName
      firstName
      surname
      email
      avatar
      jobTitle
    }
    participants {
      aggregateId
      preferredName
      firstName
      surname
      email
      avatar
      jobTitle
    }
    scopeRoots {
      workspaceId
      groupId
      group {
        id
        name
        type
        hierarchy
        hierarchyIds
      }
    }
    primaryDriver
    purpose
    successCriteria
    benefits
    workflowStage
    startDate
    targetImplementationDate
    baselineDataDate
    createdAt
    updatedAt
  }
}

query getDesignForWorkspace {
  design: getDesignForWorkspace {
    id
    tenantId
    name
    designType
    canEditPeople
    baselineWorkspaceId
    designWorkspaceId
    sponsors {
      aggregateId
      preferredName
      firstName
      surname
      email
      avatar
      jobTitle
    }
    leads {
      aggregateId
      preferredName
      firstName
      surname
      email
      avatar
      jobTitle
    }
    participants {
      aggregateId
      preferredName
      firstName
      surname
      email
      avatar
      jobTitle
    }
    scopeRoots {
      workspaceId
      groupId
      group {
        id
        name
        type
        hierarchy
        hierarchyIds
      }
    }
    primaryDriver
    purpose
    successCriteria
    benefits
    workflowStage
    startDate
    targetImplementationDate
    baselineDataDate
    createdAt
    updatedAt
  }
}

mutation createDesign($input: DesignInput!) {
  design: createDesign(input: $input) {
    id
    name
    baselineWorkspaceId
    designWorkspaceId
    workflowStage
    createdAt
    updatedAt
  }
}

mutation updateDesign($id: ID!, $input: DesignInput!) {
  design: updateDesign(id: $id, input: $input) {
    id
    name
    designType
    baselineWorkspaceId
    designWorkspaceId
    primaryDriver
    purpose
    successCriteria
    benefits
    workflowStage
    startDate
    targetImplementationDate
    baselineDataDate
    createdAt
    updatedAt
  }
}

mutation deleteDesign($id: ID!) {
  deleteDesign(id: $id) {
    deleted
    accepted
    designId
    deprovisioning {
      ...DesignDeprovisioningFields
    }
  }
}

mutation reprovisionDesign($input: ReprovisionDesignInput!) {
  reprovisionDesign(input: $input) {
    cloneId
  }
}

mutation redeprovisionDesign($input: RedeprovisionDesignInput!) {
  redeprovisionDesign(input: $input) {
    designId
  }
}
